import type { StaffRole } from '@planuze/admin-contracts';
import {
  Breadcrumb,
  Page,
  PageContent,
  PageHeader,
  PageTitleBlock,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { CompanyAuditTab } from '../components/companies/company-audit-tab.js';
import { CompanyDetailHeader } from '../components/companies/company-detail-header.js';
import { CompanyMembersTab } from '../components/companies/company-members-tab.js';
import { CompanyOverviewTab } from '../components/companies/company-overview-tab.js';
import { CompanyPackagesTab } from '../components/companies/company-packages-tab.js';
import { CompanySubscriptionTab } from '../components/companies/company-subscription-tab.js';
import { DetailPageState } from '../components/detail-page-state.js';
import { useCompanyDetailContent } from '../content/sections/company-detail.js';
import { useCompanyDetail } from '../hooks/use-companies.js';
import { useStaffSession } from '../hooks/use-staff-session.js';

const routeApi = getRouteApi('/authenticated/companies/$id');
/* Atribuir o plano da org (C6 — ADR-0422): super_admin/billing_ops (espelha o gate do
   admin-service). */
const ASSIGN_PLAN_ROLES: readonly StaffRole[] = ['super_admin', 'billing_ops'];

export function CompanyDetailPage() {
  const { id } = routeApi.useParams();
  const session = useStaffSession();
  const copy = useCompanyDetailContent();
  const canAssignPlan = session !== null && ASSIGN_PLAN_ROLES.includes(session.role);
  const { data: company, isLoading, isError } = useCompanyDetail(id);
  const navigate = useNavigate();

  /* Trilha de volta para a lista de empresas (§7.3 navegação de volta). O item corrente
     (nome da empresa, ou fallback genérico enquanto carrega/em erro) é não-clicável. */
  const renderHeader = (label: string) => (
    <PageHeader>
      <PageTitleBlock>
        <Breadcrumb
          ariaLabel={copy.breadcrumbAriaLabel}
          items={[
            {
              label: copy.companiesLabel,
              onClick: () => {
                navigate({ to: '/companies' }).catch(() => undefined);
              },
            },
            { label },
          ]}
        />
      </PageTitleBlock>
    </PageHeader>
  );

  if (isError) {
    return (
      <DetailPageState
        header={renderHeader(copy.companyFallbackLabel)}
        kind="error"
        message={copy.notFoundMessage}
      />
    );
  }
  if (isLoading || company === undefined) {
    return (
      <DetailPageState header={renderHeader(copy.companyFallbackLabel)} kind="loading" message="" />
    );
  }
  return (
    <Page>
      {renderHeader(company.name)}
      <PageContent className="space-y-6">
        <CompanyDetailHeader company={company} staffRole={session?.role ?? 'readonly'} />
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">{copy.tabs.overview}</TabsTrigger>
            <TabsTrigger value="members">{copy.tabs.members}</TabsTrigger>
            <TabsTrigger value="subscription">{copy.tabs.subscription}</TabsTrigger>
            <TabsTrigger value="packages">{copy.tabs.packages}</TabsTrigger>
            <TabsTrigger value="audit">{copy.tabs.audit}</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <CompanyOverviewTab company={company} />
          </TabsContent>
          <TabsContent value="members">
            <CompanyMembersTab members={company.members} />
          </TabsContent>
          <TabsContent value="subscription">
            <CompanySubscriptionTab
              subscription={company.subscription}
              companyId={company.id}
              companyName={company.name}
              canAssign={canAssignPlan}
            />
          </TabsContent>
          <TabsContent value="packages">
            <CompanyPackagesTab companyId={company.id} />
          </TabsContent>
          <TabsContent value="audit">
            <CompanyAuditTab audit={company.recentAudit} companyId={company.id} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}
